import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralBacklogs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [backlogs, setBacklogs] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingBacklog, setEditingBacklog] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    title: "",
    description: "",
    priority: "Medium",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // Sample Data
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Support Portal" },
  ];

  const demoBacklogs = [
    {
      id: 1,
      projectId: 1,
      title: "User Login Module",
      description: "Implement secure authentication",
      priority: "High",
    },
    {
      id: 2,
      projectId: 1,
      title: "Homepage UI",
      description: "Modern responsive homepage",
      priority: "Medium",
    },
    {
      id: 3,
      projectId: 2,
      title: "Inventory Sync",
      description: "Sync ERP inventory with warehouse",
      priority: "High",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setBacklogs(demoBacklogs);
  };

  const handleAddNew = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingBacklog(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingBacklog(item);

    setFormData({
      title: item.title,
      description: item.description || "",
      priority: item.priority,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this backlog item?")) return;

    setBacklogs((prev) => prev.filter((b) => b.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title) {
      alert("Backlog title is required");
      return;
    }

    const newBacklog = {
      id: editingBacklog?.id || Date.now(),
      projectId: Number(selectedProjectId),
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
    };

    if (editingBacklog) {
      setBacklogs((prev) =>
        prev.map((b) => (b.id === editingBacklog.id ? newBacklog : b)),
      );
    } else {
      setBacklogs((prev) => [...prev, newBacklog]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -----------------------
  // Filtering
  // -----------------------

  const filteredProjectBacklogs = backlogs.filter(
    (b) => String(b.projectId) === String(selectedProjectId),
  );

  const filtered = filteredProjectBacklogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      (b.description || "").toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const priorityColor = {
    High: "bg-red-600",
    Medium: "bg-amber-600",
    Low: "bg-emerald-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER (unchanged) */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📝 SprintCentral
          </div>
          <div className="text-xs text-gray-400">Manage Product Backlogs</div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      {/* TOOLBAR */}

      <div className="mt-4 min-h-[75vh]">
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Backlogs
          </button>

          <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Backlog
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search backlog..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg w-full"
            />
          </div>
        </div>

        {/* MODAL */}

        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingBacklog ? "Edit Backlog" : "New Backlog"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Backlog Title"
                value={formData.title}
                required
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Backlog Description"
                style={{ height: "100px", width: "100%" }}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingBacklog ? "Update Backlog" : "Create Backlog"}
              </button>
            </form>
          </div>
        )}

        {/* CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{item.title}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${priorityColor[item.priority]}`}
                >
                  {item.priority}
                </span>
              </div>

              <p className="text-sm text-gray-300 mt-2">
                {item.description || "No description"}
              </p>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION */}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* FOOTER (unchanged) */}

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralBacklogs;
